import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PlayCircleIcon, AcademicCapIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { db, auth } from '../../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';

const MyCourses = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [studentData, setStudentData] = useState(null);
    const [lessonsCounts, setLessonsCounts] = useState({ course1: 0, course2: 0 });

    // [REF: CLEAN_HELPER] - دالة التنظيف الحديدية لتوحيد النصوص ومنع أخطاء الهمزات
    const clean = (str) => (str || "").toString().trim().replace(/[أإآا]/g, "ا").toLowerCase();

    useEffect(() => {
        const fetchStudentStatus = async () => {
            if (auth.currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                    if (userDoc.exists()) {
                        const currentStudent = userDoc.data();
                        setStudentData(currentStudent);

                        const lessonsSnap = await getDocs(collection(db, "lessons"));
                        const allLessons = lessonsSnap.docs.map(doc => doc.data());

                        const count1 = allLessons.filter(l => {
                            const t = clean(l.courseType);
                            return t.includes("اول") || t.includes("1") || t === "عام";
                        }).length;

                        const count2 = allLessons.filter(l => {
                            const t = clean(l.courseType);
                            return t.includes("ثاني") || t.includes("2") || t === "عام";
                        }).length;
                        
                        setLessonsCounts({ course1: count1, course2: count2 });
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchStudentStatus();
    }, []);

    // [REF: IRON_IS_ACTIVE_CHECK] - نظام العزل الحديدي المطور لفصل الكورسات
    const checkIsActive = (target) => {
        if (!studentData) return false;
        
        const subType = clean(studentData.subscriptionType || "");
        const activeC = clean(studentData.activeCourse || "");
        
        // فحص "الوصول الكامل" (فقط إذا كان الاشتراك صريحاً ككورسين)
        const hasFull = subType.includes("كامل") || subType.includes("كورسين");

        // منطق الكورس الأول (target === "1")
        if (target === "1") {
            return subType.includes("اول") || subType.includes("1") || activeC.includes("اول") || studentData.isCourse1Active === true || hasFull;
        } 
        
        // منطق الكورس الثاني (target === "2") - القفل المطور
        if (target === "2") {
            const hasExplicitC2 = subType.includes("ثاني") || subType.includes("2") || activeC.includes("ثاني") || studentData.isCourse2Active === true || hasFull;
            return hasExplicitC2;
        }

        return false;
    };

    if (loading) return (
        <div className="h-screen flex items-center justify-center bg-transparent">
            <div className="w-10 h-10 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="max-w-6xl mx-auto space-y-10 animate-in fade-in duration-700 pb-20 text-right" dir="rtl">
            
            <div className="px-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-right">
                <div>
                    <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
                        <AcademicCapIcon className="w-10 h-10 text-[#007AFF]" /> دروسي المفعّلة
                    </h1>
                    <p className="text-gray-500 font-bold mt-1">
                        أهلاً بك يا {studentData?.name || 'بطل'}
                    </p>
                </div>
                <div className="bg-white px-4 py-2 rounded-2xl shadow-sm border border-white/50 flex items-center gap-2">
                    <SparklesIcon className="w-5 h-5 text-emerald-500" />
                    <span className="text-xs font-black text-gray-600">
                        {checkIsActive("1") && checkIsActive("2") ? "المنهج الكامل مفعل ✅" : "اشتراكك نشط حالياً ✅"}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
                {/* [REF: CARD_COURSE_1] */}
                <CourseCard 
                    title="علم الأحياء - الكورس الأول"
                    isActive={checkIsActive("1")}
                    lessonsCount={lessonsCounts.course1}
                    progress={studentData?.course1Progress || 0}
                    onNavigate={() => navigate('/student/course/course1')}
                    onActivate={() => navigate('/student/activate')}
                />

                {/* [REF: CARD_COURSE_2] */}
                <CourseCard 
                    title="علم الأحياء - الكورس الثاني"
                    isActive={checkIsActive("2")}
                    lessonsCount={lessonsCounts.course2}
                    progress={studentData?.course2Progress || 0}
                    onNavigate={() => navigate('/student/course/course2')}
                    onActivate={() => navigate('/student/activate')}
                />

                <motion.div 
                    onClick={() => navigate('/student/activate')}
                    className="md:col-span-2 border-2 border-dashed border-gray-200 rounded-[40px] p-6 flex items-center justify-center gap-4 hover:bg-white/40 transition-all cursor-pointer group"
                >
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 group-hover:text-[#007AFF]">
                        <span className="text-2xl font-black">+</span>
                    </div>
                    <p className="text-sm font-black text-gray-400">هل لديك كود تفعيل لمادة أخرى؟ اضغط هنا</p>
                </motion.div>
            </div>
        </div>
    );
};

const CourseCard = ({ title, isActive, lessonsCount, progress, onNavigate, onActivate }) => (
    <motion.div 
        whileHover={isActive && lessonsCount > 0 ? { scale: 1.02, y: -5 } : {}}
        whileTap={isActive && lessonsCount > 0 ? { scale: 0.98 } : {}}
        onClick={isActive && lessonsCount > 0 ? onNavigate : null}
        className={`ios-glass p-6 md:p-8 rounded-[45px] relative overflow-hidden border shadow-xl transition-all ${
            isActive && lessonsCount > 0 
                ? 'cursor-pointer border-white/60 bg-white/40' 
                : isActive && lessonsCount === 0
                    ? 'border-blue-500/30 bg-blue-500/5'
                    : 'opacity-80 grayscale-[0.6] bg-gray-100/20 border-gray-200'
        }`}
    >
        <div className={`absolute -top-10 -left-10 w-40 h-40 rounded-full blur-3xl ${isActive ? 'bg-blue-500/10' : 'bg-gray-500/5'}`}></div>

        <div className="flex justify-between items-start mb-6 relative z-10 text-right">
            <div className={`w-14 h-14 md:w-16 md:h-16 rounded-[22px] flex items-center justify-center text-white shadow-lg ${isActive ? 'bg-gradient-to-br from-[#007AFF] to-[#00C7FF]' : 'bg-gray-400 shadow-none'}`}>
                {isActive && lessonsCount > 0 ? <PlayCircleIcon className="w-10 h-10" /> : <LockClosedIcon className="w-8 h-8" />}
            </div>
            <div className="flex flex-col items-end gap-2">
                <span className={`text-[10px] font-black px-4 py-1.5 rounded-full ${isActive ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-gray-100 text-gray-400'}`}>
                    {isActive ? 'مفعّل ✅' : 'غير مفعّل 🔒'}
                </span>
            </div>
        </div>
        
        <div className="relative z-10 text-right">
            <h3 className={`text-xl md:text-2xl font-black mb-1 ${isActive ? 'text-gray-800' : 'text-gray-400'}`}>{title}</h3>
            <p className="text-sm text-gray-400 font-bold mb-6 md:mb-8 text-right">الأستاذة نور عماد</p>
            
            {isActive && lessonsCount > 0 ? (
                <div className="space-y-4">
                    <div className="flex justify-between items-center text-[11px] font-black">
                        <span className="text-gray-400 uppercase tracking-wider">الإنجاز</span>
                        <span className="text-[#007AFF]">{progress}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100/50 rounded-full overflow-hidden p-0.5 border border-white/50">
                        <motion.div 
                            initial={{ width: 0 }} 
                            animate={{ width: `${progress}%` }} 
                            className="h-full bg-gradient-to-r from-[#007AFF] to-[#00C7FF] rounded-full shadow-[0_0_10px_rgba(0,122,255,0.3)]" 
                        />
                    </div>
                    <div className="mt-6 pt-4 border-t border-gray-100/50 flex justify-between items-center text-[11px] font-bold">
                        <span>{lessonsCount} محاضرة</span>
                        <span className="text-[#007AFF] flex items-center gap-1">دخول ←</span>
                    </div>
                </div>
            ) : isActive && lessonsCount === 0 ? (
                <div className="pt-6 border-t border-gray-100/50 flex flex-col gap-3 text-right">
                    <p className="text-[11px] font-black text-blue-600 leading-relaxed">
                        لا توجد محاضرات حاليا بانتظار رفع المحاضرات ⏳
                    </p>
                </div>
            ) : (
                <div className="pt-6 border-t border-gray-100/50 flex flex-col gap-4 text-right">
                    <p className="text-[11px] font-bold text-gray-400">هذا المنهج غير متاح في اشتراكك الحالي.</p>
                    <button onClick={(e) => { e.stopPropagation(); onActivate(); }} className="w-full py-3 bg-gray-800 text-white rounded-2xl text-xs font-black shadow-lg">تفعيل الآن</button>
                </div>
            )}
        </div>
    </motion.div>
);

export default MyCourses;